import qs from 'qs';
import { AxiosCharts } from '../config';
import { ChartsParamsType, UpdateParamsType } from 'components/Charts universal/types';

export type DashboardChartParams = Omit<ChartsParamsType, 'Sistemas'> & {
  Sistemas?: number[];
  status?: number[];
};

// ─── VISUAL-34: os 8 gráficos novos da Visão Geral ────────────────────────────────────────────
// `Sites` (e o novo `Top`) só se aplicam aos 8 endpoints novos — em vez de alargar `Sites` no
// `DashboardChartParams` compartilhado (que quebraria ~12 gráficos legados de `Charts universal/`
// fora do dashboard, que montam `Sites` como escalar direto de `headerFilters?.units?.id`), este
// tipo isola a diferença: o backend (VISUAL-34) sempre aceitou `Sites` como array (interseção de
// filtros via `RefreshFiltroDashBoard`) — a tela hoje só permite 1 unidade por vez, então os hooks
// que chamam esses 8 endpoints empacotam o valor escalar num array de 1 (ver `toV34Params`).
// `Top` só tem efeito em `ranking-atividades-por-unidade` e `ranking-horas-tecnicas-por-tecnico`
// (default 10 no backend se omitido).
export type DashboardChartV34Params = Omit<DashboardChartParams, 'Sites'> & {
  Sites?: number[];
  Top?: number;
};

// Adapta os params já montados por `useDashboardParams` (compartilhados com os gráficos antigos,
// `Sites` escalar) para o shape que os 8 endpoints novos esperam (`Sites` array). Único ponto de
// conversão — os 8 hooks chamam isso antes de repassar pro service, em vez de repetir a mesma
// checagem em cada um.
export function toV34Params(params: DashboardChartParams, extra?: { Top?: number }): DashboardChartV34Params {
  return {
    ...params,
    Sites: params.Sites != null ? [params.Sites] : undefined,
    ...extra,
  };
}

// Tipos próprios (não reaproveitam os tipos do mock antigo em VisãoGeral/mocks/) porque o shape
// real do backend difere em nomes de campo em vários pontos — ver VISUAL-34-dashboard-endpoints.md
// seção 3. Criar um tipo novo por resposta evita forçar os dois shapes a coincidir.
export type PorPrazoResponse = {
  total: number;
  pendentes: number;
  pendentesProgramadas: number;
  pendentesAtrasadas: number;
  realizadas: number;
  realizadasDentroPrazo: number;
  realizadasForaPrazo: number;
};

export type RankingAtividadesPorUnidadeResponse = {
  siteId: number;
  siteNome: string;
  estado: string | null;
  pendentes: number;
  realizadas: number;
  total: number;
};

export type DistribuicaoHorasTecnicasResponse = {
  totalHoras: number;
  horasAtividades: number;
  horasOcorrencias: number;
  equipamentosAtividades: number;
  equipamentosOcorrencias: number;
};

export type HorasTecnicasPorSemanaResponse = {
  periodo: string; // ex.: 'S.31' — rótulo pronto, não usar como chave única (repete entre anos)
  horasAtividades: number;
  horasOcorrencias: number;
  totalHoras: number;
};

export type RankingHorasTecnicasPorTecnicoResponse = {
  tecnicoId: number;
  tecnicoNome: string;
  horasPrevistas: number;
  horasRealizadas: number;
};

export type OcorrenciasCadastradasSolucionadasPorSemanaResponse = {
  periodo: string; // ex.: 'S.31'
  cadastradas: number;
  solucionadas: number;
};

export type OcorrenciasPorSistemaPercentualResponse = {
  sistemaEmpresaId: number | null; // null na linha "Sem sistema informado"
  sistema: string;
  quantidade: number;
  percentual: number; // 0-100, já calculado pelo backend
};

const BASE = '/api/v1/DashBoard';

const serialize = (params: unknown) => qs.stringify(params, { arrayFormat: 'repeat' });

const DashboardChartsService = {
  getOcorrenciasPorSistema: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/ocorrencias-por-sistema`, { params, paramsSerializer: serialize }),

  getOcorrenciasPorPrioridade: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/ocorrencias-por-prioridade-por-periodo`, { params, paramsSerializer: serialize }),

  getQuantidadeOcorrenciasPorPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/quantidade-ocorrencias-por-periodo`, { params, paramsSerializer: serialize }),

  getAtividadesPorStatusPorPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/atividades-por-status-por-periodo`, { params, paramsSerializer: serialize }),

  getEntradaSaidaOcorrenciasPorPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/entrada-saida-ocorrencias-por-periodo`, { params, paramsSerializer: serialize }),

  getOcorrenciasPorStatusCorrecaoSemana: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/quantidade-ocorrencias-por-status-correcao-semana`, {
      params,
      paramsSerializer: serialize,
    }),

  getAnaliseFalhasSDai: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/analise-falhas-sdai`, { params, paramsSerializer: serialize }),

  getDisponibilidadePorSistemaPorPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/disponibilidade-por-sistema-por-periodo`, { params, paramsSerializer: serialize }),

  getHorasPreventivaseCorretivasPorPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/horas-preventivas-corretivas-por-periodo`, { params, paramsSerializer: serialize }),

  getHorasPorPeriodoPorSistema: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/horas-por-periodo-por-sistema`, { params, paramsSerializer: serialize }),

  getHorasPreventivaseCorretivasTotal: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/horas-preventivas-corretivas-total`, { params, paramsSerializer: serialize }),

  getAnaliseFalhasSDaiPorPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/analise-falhas-sdai-por-periodo`, { params, paramsSerializer: serialize }),

  getEntradaOcorrenciasPorSistemaPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/entrada-ocorrencias-por-sistema-periodo`, { params, paramsSerializer: serialize }),

  getSaidaOcorrenciasPorSistemaPeriodo: (params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}/saida-ocorrencias-por-sistema-periodo`, { params, paramsSerializer: serialize }),

  // Para componentes que recebem httpPath como prop (ex: OccurrencesRequiredBySystem, SDAIFailuresAnalysisByPeriod)
  getByPath: (path: string, params: DashboardChartParams) =>
    AxiosCharts.get(`${BASE}${path}`, { params, paramsSerializer: serialize }),

  // Horas Técnicas: última atualização e trigger de atualização
  getUltimaAtualizacaoHorasTecnicas: (params: UpdateParamsType) =>
    AxiosCharts.get(`${BASE}/ultima-atualizacao-dm-horas-tecnicas-por-sistema`, {
      params,
      paramsSerializer: serialize,
    }),

  triggerAtualizacaoHorasTecnicas: (params: UpdateParamsType) =>
    AxiosCharts.get(`${BASE}/atualizar-dm-horas-tecnicas-por-sistema`, { params, paramsSerializer: serialize }),

  // VISUAL-34: os 8 gráficos novos da Visão Geral (personalização) — backend publicado em
  // homologação (commit d3a798d, PR #89 do repo `laschart`).
  getOcorrenciasPorPrazo: (params: DashboardChartV34Params) =>
    AxiosCharts.get<PorPrazoResponse>(`${BASE}/ocorrencias-por-prazo`, { params, paramsSerializer: serialize }),

  getAtividadesPorPrazo: (params: DashboardChartV34Params) =>
    AxiosCharts.get<PorPrazoResponse>(`${BASE}/atividades-por-prazo`, { params, paramsSerializer: serialize }),

  getRankingAtividadesPorUnidade: (params: DashboardChartV34Params) =>
    AxiosCharts.get<RankingAtividadesPorUnidadeResponse[]>(`${BASE}/ranking-atividades-por-unidade`, {
      params,
      paramsSerializer: serialize,
    }),

  getDistribuicaoHorasTecnicas: (params: DashboardChartV34Params) =>
    AxiosCharts.get<DistribuicaoHorasTecnicasResponse>(`${BASE}/distribuicao-horas-tecnicas`, {
      params,
      paramsSerializer: serialize,
    }),

  getDistribuicaoHorasTecnicasPorSemana: (params: DashboardChartV34Params) =>
    AxiosCharts.get<HorasTecnicasPorSemanaResponse[]>(`${BASE}/distribuicao-horas-tecnicas-por-semana`, {
      params,
      paramsSerializer: serialize,
    }),

  getRankingHorasTecnicasPorTecnico: (params: DashboardChartV34Params) =>
    AxiosCharts.get<RankingHorasTecnicasPorTecnicoResponse[]>(`${BASE}/ranking-horas-tecnicas-por-tecnico`, {
      params,
      paramsSerializer: serialize,
    }),

  getOcorrenciasCadastradasSolucionadasPorSemana: (params: DashboardChartV34Params) =>
    AxiosCharts.get<OcorrenciasCadastradasSolucionadasPorSemanaResponse[]>(
      `${BASE}/ocorrencias-cadastradas-solucionadas-por-semana`,
      { params, paramsSerializer: serialize },
    ),

  getOcorrenciasPorSistemaPercentual: (params: DashboardChartV34Params) =>
    AxiosCharts.get<OcorrenciasPorSistemaPercentualResponse[]>(`${BASE}/ocorrencias-por-sistema-percentual`, {
      params,
      paramsSerializer: serialize,
    }),
};

export { DashboardChartsService };
